import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Search, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useAdminErrorGroups } from '../api/admin-api'

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'open', label: '미해결' },
  { value: 'resolved', label: '해결됨' },
]

const DAYS_OPTIONS = [
  { value: '', label: '전체 기간' },
  { value: '1', label: '24시간' },
  { value: '7', label: '7일' },
  { value: '30', label: '30일' },
]

export function ErrorList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Number(searchParams.get('page')) || 1
  const search = searchParams.get('search') || ''
  const status = searchParams.get('status') || ''
  const days = searchParams.get('days') || ''
  const [searchInput, setSearchInput] = useState(search)
  const pageSize = 20

  const { data, isLoading, error } = useAdminErrorGroups({
    page,
    pageSize,
    status: status || undefined,
    search: search || undefined,
    days: days ? Number(days) : undefined,
  })

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 0

  const setParam = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set(key, value)
    } else {
      params.delete(key)
    }
    if (key !== 'page') params.set('page', '1')
    setSearchParams(params)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setParam('search', searchInput)
  }

  if (isLoading) return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  if (error) return <div className="text-center py-12 text-red-500">에러: {error.message}</div>

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">오류 리포트</h1>
        <p className="text-slate-500 mt-2 leading-relaxed">
          브라우저에서 수집된 프론트엔드 오류 (같은 원인끼리 그룹화)
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
          <Input
            placeholder="에러명·메시지·URL 검색..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button type="submit" variant="secondary">
            <Search className="w-4 h-4" />
          </Button>
        </form>

        <div className="flex gap-1">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`px-3 py-1.5 text-sm rounded-lg transition ${
                status === opt.value
                  ? 'bg-slate-900 text-white font-semibold'
                  : 'text-slate-500 hover:bg-gray-100'
              }`}
              onClick={() => setParam('status', opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex gap-1">
          {DAYS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`px-3 py-1.5 text-sm rounded-lg transition ${
                days === opt.value
                  ? 'bg-slate-900 text-white font-semibold'
                  : 'text-slate-500 hover:bg-gray-100'
              }`}
              onClick={() => setParam('days', opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">에러</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[72px]">횟수</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[88px]">사용자</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">마지막 발생</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[88px]">빌드</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[96px]">상태</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data?.items.map((group) => (
              <tr
                key={group.fingerprint}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => group.latest && navigate(`/admin/errors/${group.latest.id}`)}
              >
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">
                    {group.latest?.name ?? '(알 수 없음)'}
                  </div>
                  <div className="text-sm text-gray-500 max-w-md truncate">
                    {group.latest?.message}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm tabular-nums text-gray-700">{group.count}</td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center gap-1 text-sm tabular-nums text-gray-500">
                    <Users className="h-3.5 w-3.5" />
                    {group.userCount}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                  {formatDate(group.lastSeen)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                  {group.latest?.buildId ?? '-'}
                </td>
                <td className="px-6 py-4">
                  {group.latest?.status === 'resolved' ? (
                    <Badge className="bg-green-100 text-green-700">해결됨</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700">미해결</Badge>
                  )}
                </td>
              </tr>
            ))}
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  수집된 오류가 없습니다
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data && totalPages > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">총 {data.total}개 그룹</div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setParam('page', String(Math.max(1, page - 1)))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">
              {page} / {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setParam('page', String(Math.min(totalPages, page + 1)))}
              disabled={page >= totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
